import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useApiClient } from '@/api/client'
import { useAuth } from '@/auth/useAuth'
import { useWorkspaceEvents } from '@/api/realtime/useWorkspaceEvents'
import { usePeople } from '@/api/people/usePeople'
import { useUser } from '@/auth/UserContext'
import { Sentry } from '@/monitoring/sentry'

export type Workspace = {
  id: string
  slug: string
  name: string
  team_size?: string | null
  logo_url?: string | null
  has_demo_data?: boolean
  settings?: { meetings_enabled?: boolean } & Record<string, unknown>
  deleted_at?: string | null
  delete_scheduled_for?: string | null
}

export type WorkspaceMember = {
  workspace_id: string
  user_id: string
  status: string
  is_owner: boolean
  access_mode: 'full' | 'guest' | string
  roles: string[]
}

export type SubscriptionPlan = {
  id: string
  name_ru: string
  limits: Record<string, number | null>
  features: Record<string, boolean>
}

export type WorkspaceSubscription = {
  id: string
  plan_id: string
  status: string
  override_reason?: string | null
  override_until?: string | null
  usage_users_count: number
  usage_storage_bytes: number
  usage_ai_credits_used: number
  usage_ai_smart_credits_used: number
  plan: SubscriptionPlan
}

export type WorkspaceSummary = Workspace & {
  role: string
  plan_id: string
  plan_name: string
}

type WorkspaceContextValue = {
  workspace: Workspace
  membership: WorkspaceMember
  subscription: WorkspaceSubscription
  memberRoles: string[]
  memberPermissions: string[]
  workspaces: WorkspaceSummary[]
  refetchWorkspaces: () => Promise<void>
  updateWorkspaceLocal: (workspace: Workspace) => void
  hasPermission: (permission: string) => boolean
  path: (to: string) => string
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null)

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { workspace_slug } = useParams()
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { fetchWithAuth } = useApiClient()
  const fetchRef = useRef(fetchWithAuth)

  const [state, setState] = useState<{
    workspace: Workspace
    membership: WorkspaceMember
    subscription: WorkspaceSubscription
    memberRoles: string[]
    memberPermissions: string[]
  } | null>(null)
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchRef.current = fetchWithAuth
  }, [fetchWithAuth])

  const refetchWorkspaces = useCallback(async () => {
    const data = await fetchRef.current<{ items: WorkspaceSummary[] }>('/api/workspaces')
    setWorkspaces(data.items)
  }, [])

  const updateWorkspaceLocal = useCallback((nextWorkspace: Workspace) => {
    setState((current) =>
      current ? { ...current, workspace: nextWorkspace } : current
    )
    setWorkspaces((current) =>
      current.map((item) =>
        item.id === nextWorkspace.id ? { ...item, ...nextWorkspace } : item
      )
    )
  }, [])

  useEffect(() => {
    let cancelled = false

    async function load() {
      if (!workspace_slug) return
      setError(null)
      try {
        const workspacesData = await fetchRef.current<{ items: WorkspaceSummary[] }>('/api/workspaces')
        if (cancelled) return
        setWorkspaces(workspacesData.items)

        if (workspacesData.items.length === 0) {
          setError('workspace_not_found')
          return
        }

        const canAccess = workspacesData.items.some((item) => item.slug === workspace_slug)
        if (!canAccess) {
          const fallback = workspacesData.items[0]
          if (fallback && fallback.slug !== workspace_slug) {
            window.localStorage.setItem('chrona:last-workspace-slug', fallback.slug)
            navigate(`/${fallback.slug}/home`, { replace: true })
            return
          }
        }

        const ctx = await fetchRef.current<{
          workspace: Workspace
          membership: WorkspaceMember
          subscription: WorkspaceSubscription
          member_roles?: string[]
          member_permissions?: string[]
        }>(`/api/workspaces/${workspace_slug}/`)
        if (!cancelled) {
          setState({
            workspace: ctx.workspace,
            membership: ctx.membership,
            subscription: ctx.subscription,
            memberRoles: ctx.member_roles ?? [],
            memberPermissions: ctx.member_permissions ?? [],
          })
        }
      } catch (err) {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Ошибка загрузки workspace'

        if (message.includes('workspace_not_found') || message.includes('not_a_member')) {
          try {
            const workspacesData = await fetchRef.current<{ items: WorkspaceSummary[] }>('/api/workspaces')
            if (cancelled) return
            setWorkspaces(workspacesData.items)
            const fallback =
              workspacesData.items.find((item) => item.slug !== workspace_slug) ??
              workspacesData.items[0]
            if (fallback && fallback.slug !== workspace_slug) {
              window.localStorage.setItem('chrona:last-workspace-slug', fallback.slug)
              navigate(`/${fallback.slug}/home`, { replace: true })
              return
            }
          } catch {
            // fall through to error state
          }
        }

        setError(message)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [workspace_slug, navigate])

  useEffect(() => {
    if (state?.workspace.slug) {
      Sentry.setTag('workspace', state.workspace.slug)
    }
  }, [state?.workspace.slug])

  const value = useMemo<WorkspaceContextValue | null>(() => {
    if (!state) return null
    const permissions = new Set(state.memberPermissions)
    const isOwner = state.membership.is_owner
    return {
      ...state,
      workspaces,
      refetchWorkspaces,
      updateWorkspaceLocal,
      hasPermission(permission: string) {
        if (permissions.has(permission)) return true

        // Owner receives broad access even if permission snapshot is missing.
        if (isOwner) return true

        // Legacy fallback for older backend responses without permission snapshots.
        const legacyRoles = new Set(state.memberRoles.length > 0 ? state.memberRoles : state.membership.roles)
        if (legacyRoles.has('admin')) {
          return permission !== 'workspace:delete' && permission !== 'workspace:billing'
        }
        if (legacyRoles.has('billing')) {
          return permission === 'workspace:billing'
        }
        if (state.membership.access_mode !== 'guest') {
          return permission === 'projects:create' || permission === 'projects:view_all'
        }
        return permission === 'projects:view_all'
      },
      path(to: string) {
        const normalized = to.startsWith('/') ? to : `/${to}`
        if (normalized === '/') return `/${state.workspace.slug}/home`
        return `/${state.workspace.slug}${normalized}`
      },
    }
  }, [state, workspaces, refetchWorkspaces, updateWorkspaceLocal])

  if (error) {
    const denied = error.includes('not_a_member') || error.includes('permission')
    return (
      <div className="flex min-h-screen items-center justify-center px-4 text-center">
        <div>
          <h1 className="text-xl font-semibold">
            {denied ? 'Доступ запрещён' : 'Workspace не найден'}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {denied
              ? 'У вас нет доступа к этому workspace.'
              : 'Проверьте URL или выберите другой workspace.'}
          </p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <a className="inline-flex rounded-md border px-3 py-2 text-sm" href="/">
              На главную
            </a>
            <button
              type="button"
              className="inline-flex rounded-md border px-3 py-2 text-sm"
              onClick={logout}
            >
              Выйти
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!value) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Загрузка workspace...
      </div>
    )
  }

  return (
    <WorkspaceContext.Provider value={value}>
      <WorkspaceEventsConnector slug={value.workspace.slug} />
      {children}
    </WorkspaceContext.Provider>
  )
}

/** Thin component so the SSE hook runs only when workspace is fully loaded. */
function WorkspaceEventsConnector({ slug }: { slug: string }) {
  const { user } = useUser()
  const { peopleByUserId } = usePeople()

  const currentPersonId = useMemo(
    () => (user ? (peopleByUserId.get(user.id)?.id ?? null) : null),
    [user, peopleByUserId],
  )

  useWorkspaceEvents(slug, currentPersonId)
  return null
}

export function useWorkspace() {
  const value = useContext(WorkspaceContext)
  if (!value) throw new Error('useWorkspace must be used within WorkspaceProvider')
  return value
}
